import { ColumnFiltersState } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import { useSession } from 'next-auth/react';
import useSWR from 'swr';

import { DURATION, NEXT_AUTH_STATUSES } from '@/lib/constants';
import { TrackingRequestsListResponse } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { getTrackingRequestsUrl } from '@/utils/helpers/api-urls';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';

const ERROR_MESSAGE = 'Error fetching tracking requests';

interface UseTrackingRequestsProps {
  columnFilters?: ColumnFiltersState;
  isEnabled?: boolean;
  countOnly?: boolean;
}

export const useTrackingRequests = ({
  columnFilters,
  isEnabled = true,
  countOnly = false,
}: UseTrackingRequestsProps = {}) => {
  const { status } = useSession();
  const isAuthenticated = status === NEXT_AUTH_STATUSES.AUTHENTICATED;

  const filters: Record<string, any> | undefined = (() => {
    const base = { countOnly };

    if (!isEmpty(columnFilters)) {
      return { ...base, [QUERY_PARAM.FILTERS]: JSON.stringify(columnFilters) };
    }
    return base;
  })();

  const getUrl = () => {
    if (!isEnabled || !isAuthenticated) return null;

    const baseUrl = getTrackingRequestsUrl();
    return filters ? `${baseUrl}?${new URLSearchParams(filters)}` : baseUrl;
  };

  const { error, isLoading, data } = useSWR<TrackingRequestsListResponse>(getUrl(), request, {
    keepPreviousData: true,
    errorRetryCount: 1,
    dedupingInterval: DURATION.IN_MILLISECONDS.THIRTY_SECONDS,
    refreshInterval: DURATION.IN_MILLISECONDS.TEN_MINS,
    revalidateOnFocus: false,
  });

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  return {
    isLoading,
    error: errorMessage,
    trackingRequests: data?.trackingRequests,
    count: data?.count,
  };
};
